import React, { useState, useRef, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faCamera, 
  faExclamationTriangle,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { 
  faInstagram, 
  faTwitter, 
  faFacebook, 
  faYoutube,
  faLinkedin 
} from '@fortawesome/free-brands-svg-icons';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import { 
  collection, 
  addDoc, 
  doc, 
  updateDoc, 
  arrayUnion, 
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { checkUsernameAvailability } from '../../utils/usernameHelpers';
import { UserContext } from '../../context/UserContext';
import styles from './BandModal.module.css';
import ImageCropper from '../common/ImageCropper';
import { uploadBandImage } from '../../utils/ImageUtils';
import { createBandNotification, NOTIFICATION_TYPES } from '../../utils/NotificationService';
import defaultBandPic from '../../assets/Images/defaultUserProfilePicture.png';
import defaultCoverPhoto from '../../assets/Images/defaultUserCoverPicture.png';
import appleMusicLogo from '../../assets/Images/applemusicicon.png';
import spotifyLogo from '../../assets/Images/spotifyicon.png';

// Define genre options (alphabetically sorted)
const GENRE_OPTIONS = [
  'Alternative', 'Blues', 'Classical', 'Country', 'Dance', 'Electronic',
  'Folk', 'Funk', 'Gospel', 'Grunge', 'Hip Hop', 'Indie', 'Jazz', 'Latin',
  'Metal', 'New Age', 'Opera', 'Pop', 'Progressive', 'Psychedelic', 'Punk',
  'R&B', 'Rap', 'Reggae', 'Reggaeton', 'Rock', 'Salsa', 'Ska', 'Soul',
  'Techno', 'Trance', 'World Music'
].sort();

const BandModal = ({ isOpen, onClose, onSuccess }) => {
  const { userData, setUserData } = useContext(UserContext);
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [usernameChecking, setUsernameChecking] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  
  // Form data state (removed assignedTours)
  const [formData, setFormData] = useState({
    bandFullName: '',
    bandUserName: '',
    bandBio: '',
    bandEmail: '',
    bandWebsite: '',
    city: '',
    state: '',
    country: '',
    bandGenres: [],
    managerName: userData?.fullName || '',
    managerEmail: userData?.email || '',
    managerPhone: '',
    socialLinks: {
      instagramUrl: '',
      twitterUrl: '',
      facebookUrl: '',
      youtubeUrl: '',
      linkedInUrl: '',
      spotifyUrl: '',
      appleMusicUrl: ''
    }
  });

  // Image states
  const [profilePicture, setProfilePicture] = useState({
    file: null,
    preview: null,
    cropData: null
  });
  
  const [coverPhoto, setCoverPhoto] = useState({
    file: null,
    preview: null,
    cropData: null
  });

  const [showProfileCropper, setShowProfileCropper] = useState(false);
  const [showCoverCropper, setShowCoverCropper] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [cropData, setCropData] = useState(null);

  // Refs for file inputs
  const profilePicRef = useRef(null);
  const coverPhotoRef = useRef(null);

  // Form input handlers
  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }

    // Special handling for username
    if (name === 'bandUserName') {
      // Auto-lowercase and remove spaces
      const sanitizedValue = value.toLowerCase().replace(/\s+/g, '');
      
      if (sanitizedValue !== value) {
        setFormData(prev => ({
          ...prev,
          [name]: sanitizedValue
        }));
      }

      // Check username availability after typing stops
      if (sanitizedValue.length >= 3) {
        setUsernameChecking(true);
        setUsernameAvailable(null);
        
        const timeoutId = setTimeout(async () => {
          try {
            const isAvailable = await checkUsernameAvailability(sanitizedValue);
            setUsernameAvailable(isAvailable);
          } catch (error) {
            console.error('Error checking username:', error);
            setUsernameAvailable(null);
          } finally {
            setUsernameChecking(false);
          }
        }, 500);
        
        return () => clearTimeout(timeoutId);
      } else {
        setUsernameAvailable(null);
      }
    }
  };

  const handleSocialLinkChange = (platform, value) => {
    setFormData(prev => ({
      ...prev,
      socialLinks: {
        ...prev.socialLinks,
        [platform]: value
      }
    }));
  };

  const handleGenreToggle = (genre) => {
    setFormData(prev => {
      const genres = prev.bandGenres.includes(genre)
        ? prev.bandGenres.filter(g => g !== genre)
        : [...prev.bandGenres, genre];
      return { ...prev, bandGenres: genres };
    });
  };

  // Image handling functions
  const handleProfilePicClick = () => {
    profilePicRef.current?.click();
  };

  const handleCoverPhotoClick = () => {
    coverPhotoRef.current?.click();
  };

  const handleProfilePicChange = (e) => {
    if (e.target.files?.[0]) {
      const file = e.target.files[0];
      const preview = URL.createObjectURL(file);
      setSelectedImage({
        file,
        preview,
        isProfile: true
      });
      setShowProfileCropper(true);
    }
  };

  const handleCoverPhotoChange = (e) => {
    if (e.target.files?.[0]) {
      const file = e.target.files[0];
      const preview = URL.createObjectURL(file);
      setSelectedImage({
        file,
        preview,
        isProfile: false
      });
      setShowCoverCropper(true);
    }
  };

  const handleCropComplete = async (croppedArea, croppedAreaPixels) => {
    setCropData(croppedAreaPixels);
  };

  const handleSaveCrop = async () => {
    if (!selectedImage || !cropData) {
      console.error('Missing required data for crop');
      return;
    }
  
    try {
      setIsLoading(true);
      const isProfile = selectedImage.isProfile;
      const imageFile = selectedImage.file;
  
      // Create a local preview URL instead of uploading
      const preview = URL.createObjectURL(imageFile);
  
      // Just update the state with file, crop data, and preview
      if (isProfile) {
        setProfilePicture(prev => ({
          ...prev,
          file: imageFile,
          preview: preview,
          cropData: cropData
        }));
        setShowProfileCropper(false);
      } else {
        setCoverPhoto(prev => ({
          ...prev,
          file: imageFile,
          preview: preview,
          cropData: cropData
        }));
        setShowCoverCropper(false);
      }
  
      if (selectedImage.preview) {
        URL.revokeObjectURL(selectedImage.preview);
      }
    } catch (error) {
      console.error('Error processing image:', error);
      setErrors(prev => ({
        ...prev,
        image: 'Failed to process image'
      }));
    } finally {
      setIsLoading(false);
      setSelectedImage(null);
      setCropData(null);
    }
  };

  // Validation state
  const [errors, setErrors] = useState({});

  // Validation functions
  const validateStep1 = () => {
    const newErrors = {};
    
    if (!formData.bandFullName) {
      newErrors.bandFullName = 'Band name is required';
    }
    
    if (!formData.bandUserName) {
      newErrors.bandUserName = 'Username is required';
    } else if (formData.bandUserName.length < 3) {
      newErrors.bandUserName = 'Username must be at least 3 characters';
    } else if (!/^[a-z0-9_]+$/.test(formData.bandUserName)) {
      newErrors.bandUserName = 'Username can only contain lowercase letters, numbers, and underscores';
    } else if (usernameAvailable === false) {
      newErrors.bandUserName = 'This username is already taken';
    }
    
    if (formData.bandGenres.length === 0) {
      newErrors.bandGenres = 'Please select at least one genre';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    
    if (formData.bandEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.bandEmail)) {
      newErrors.bandEmail = 'Please enter a valid email address';
    }
    
    if (formData.bandWebsite && !/^(https?:\/\/)?([\w-]+(\.[\w-]+)+)([\w.,@?^=%&:/~+#-]*[\w@?^=%&/~+#-])?$/.test(formData.bandWebsite)) {
      newErrors.bandWebsite = 'Please enter a valid website URL';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors = {};
    
    if (!formData.managerName) {
      newErrors.managerName = 'Contact name is required';
    }
    
    if (!formData.managerEmail) {
      newErrors.managerEmail = 'Contact email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.managerEmail)) {
      newErrors.managerEmail = 'Please enter a valid email address';
    }
    
    if (!formData.managerPhone) {
      newErrors.managerPhone = 'Contact phone is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateSocialLinks = () => {
    const newErrors = {};
    const urlRegex = /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)([\w.,@?^=%&:/~+#-]*[\w@?^=%&/~+#-])?$/;
    
    Object.entries(formData.socialLinks).forEach(([key, value]) => {
      if (value && !urlRegex.test(value)) {
        newErrors[`socialLinks.${key}`] = 'Please enter a valid URL';
      }
    });
    
    setErrors(prev => ({
      ...prev,
      ...newErrors
    }));
    
    return Object.keys(newErrors).length === 0;
  };

  // Step navigation
  const handleNextStep = async () => {
    let isValid = false;
    
    switch (activeStep) {
      case 1:
        isValid = validateStep1();
        break;
      case 2:
        isValid = validateStep2();
        break;
      case 3:
        isValid = validateStep3();
        break;
      case 4:
        isValid = validateSocialLinks();
        break;
      case 5:
        // Photo upload is optional, so we'll consider it always valid
        isValid = true;
        break;
      default:
        isValid = true;
    }
    
    if (isValid) {
      if (activeStep < 5) { // Changed from 6 to 5 since we removed the tour step
        setActiveStep(activeStep + 1);
      } else {
        await handleSubmit();
      }
    }
  };

  const handlePrevStep = () => {
    if (activeStep > 1) {
      setActiveStep(activeStep - 1);
    }
  };

  // Final submit handler
  const handleSubmit = async () => {
    setIsLoading(true);
  
    try {
      // Final validation check
      if (
        !validateStep1() || 
        !validateStep2() || 
        !validateStep3() || 
        !validateSocialLinks()
      ) {
        setIsLoading(false);
        return;
      }
      
      const db = getFirestoreInstance();
      
      const timestamp = Timestamp.now();

      // Prepare initial member (the creator with admin role)
      const creatorMember = {
        userId: userData.userId,
        userName: userData.userName,
        fullName: userData.fullName,
        profilePictureUrl: userData.profilePictureUrl,
        userType: userData.userType,
        role: 'admin',
        joinedAt: timestamp
      };

      // Create band document with default images first
      const bandDoc = await addDoc(collection(db, 'bands'), {
        ...formData,
        bandProfilePictureUrl: '',
        bandCoverPictureUrl: '',
        userType: 'band', // Set userType to 'band'
        createdAt: serverTimestamp(),
        createdBy: {
          userId: userData.userId,
          userName: userData.userName,
          fullName: userData.fullName,
          profilePictureUrl: userData.profilePictureUrl
        },
        // Single members array with role designation
        members: [creatorMember],
        memberRequests: [],
        // Initialize counts
        toursCount: 0,
        showsCount: 0
      });
  
      // Handle image uploads with actual band ID
      const imageUpdates = {};
  
      if (profilePicture.file) {
        const profileUrl = await uploadBandImage(
          profilePicture.file,
          profilePicture.cropData,
          userData.userId,
          bandDoc.id,
          true // isProfile
        );
        imageUpdates.bandProfilePictureUrl = profileUrl;
      }
  
      if (coverPhoto.file) {
        const coverUrl = await uploadBandImage(
          coverPhoto.file,
          coverPhoto.cropData,
          userData.userId,
          bandDoc.id,
          false // not profile
        );
        imageUpdates.bandCoverPictureUrl = coverUrl;
      }
  
      // Update band document with image URLs if needed
      if (Object.keys(imageUpdates).length > 0) {
        await updateDoc(doc(db, 'bands', bandDoc.id), imageUpdates);
      }
  
      // Create band creation notification in the band's notifications subcollection
      await createBandNotification({
        bandId: bandDoc.id,
        type: NOTIFICATION_TYPES.BAND_CREATED,
        title: 'Band Page Created',
        message: `${userData.fullName} created the band page for ${formData.bandFullName}`,
        recipientIds: [userData.userId],
        actionBy: {
          userId: userData.userId,
          userName: userData.userName,
          fullName: userData.fullName,
          profilePictureUrl: userData.profilePictureUrl
        }
      });
  
      // Update user's associated acts
      const userRef = doc(db, 'users', userData.userId);
      const newAssociatedAct = {
        id: bandDoc.id,
        type: 'band',
        name: formData.bandFullName,
        username: formData.bandUserName,
        photoUrl: imageUpdates.bandProfilePictureUrl || '',
        genres: formData.bandGenres || [],
        role: 'admin'
      };
  
      await updateDoc(userRef, {
        associatedActs: arrayUnion(newAssociatedAct)
      });
  
      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess(bandDoc.id, formData.bandUserName);
      }
  
      // Close modal
      onClose();
    } catch (error) {
      console.error('Error creating band:', error);
      setErrors(prev => ({
        ...prev,
        submit: error.message || 'Failed to create band page. Please try again.'
      }));
    } finally {
      setIsLoading(false);
    }
  };

  // Reset form when modal is closed
  const resetForm = () => {
    setFormData({
      bandFullName: '',
      bandUserName: '',
      bandBio: '',
      bandEmail: '',
      bandWebsite: '',
      city: '',
      state: '',
      country: '',
      bandGenres: [],
      managerName: userData?.fullName || '',
      managerEmail: userData?.email || '',
      managerPhone: '',
      socialLinks: {
        instagramUrl: '',
        twitterUrl: '',
        facebookUrl: '',
        youtubeUrl: '',
        linkedInUrl: '',
        spotifyUrl: '',
        appleMusicUrl: ''
      }
    });
    setProfilePicture({
      file: null,
      preview: defaultBandPic,
      cropData: null
    });
    setCoverPhoto({
      file: null,
      preview: defaultCoverPhoto,
      cropData: null
    });
    setActiveStep(1);
    setErrors({});
    setUsernameAvailable(null);
  };
  
  // Handle modal close
  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2>Create Band Page</h2>
          <button className={styles.closeButton} onClick={handleClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className={styles.progressWrapper}>
          <div className={styles.progressBar}>
            <div 
              className={styles.progressFill} 
              style={{ width: `${(activeStep / 5) * 100}%` }} // Changed from 6 to 5
            ></div>
          </div>
          <div className={styles.stepIndicators}>
            {[1, 2, 3, 4, 5].map(step => ( // Changed from [1, 2, 3, 4, 5, 6] to [1, 2, 3, 4, 5]
              <div 
                key={step}
                className={`${styles.stepIndicator} ${activeStep >= step ? styles.active : ''}`}
              >
                {step}
              </div>
            ))}
          </div>
          <div className={styles.stepLabels}>
            <div className={`${styles.stepLabel} ${activeStep === 1 ? styles.current : ''}`}>
              Basic Info
            </div>
            <div className={`${styles.stepLabel} ${activeStep === 2 ? styles.current : ''}`}>
              Contact Info
            </div>
            <div className={`${styles.stepLabel} ${activeStep === 3 ? styles.current : ''}`}>
              Management
            </div>
            <div className={`${styles.stepLabel} ${activeStep === 4 ? styles.current : ''}`}>
              Social Media
            </div>
            <div className={`${styles.stepLabel} ${activeStep === 5 ? styles.current : ''}`}>
              Upload Photos
            </div>
          </div>
        </div>

        <div className={styles.formContainer}>
          {/* Step 1: Basic Information */}
          {activeStep === 1 && (
            <div className={styles.formStep}>
              <h3>Band Basic Information</h3>
              
              <div className={styles.field}>
                <label>Band Name *</label>
                <input
                  type="text"
                  value={formData.bandFullName}
                  onChange={(e) => handleInputChange('bandFullName', e.target.value)}
                  className={errors.bandFullName ? styles.fieldError : ''}
                />
                {errors.bandFullName && (
                  <div className={styles.errorMessage}>{errors.bandFullName}</div>
                )}
              </div>

              <div className={styles.field}>
                <label>Username *</label>
                <input
                  type="text"
                  value={formData.bandUserName}
                  onChange={(e) => handleInputChange('bandUserName', e.target.value)}
                  className={errors.bandUserName ? styles.fieldError : ''}
                />
                {!errors.bandUserName && usernameChecking && (
                  <div className={styles.checkingMessage}>Checking availability...</div>
                )}
                {!errors.bandUserName && !usernameChecking && usernameAvailable === true && (
                  <div className={styles.successMessage}>Username is available</div>
                )}
                {!errors.bandUserName && !usernameChecking && usernameAvailable === false && (
                  <div className={styles.errorMessage}>Username is already taken</div>
                )}
                {errors.bandUserName ? (
                  <div className={styles.errorMessage}>{errors.bandUserName}</div>
                ) : (
                  <div className={styles.helperText}>
                    Only lowercase letters, numbers, and underscore (_) allowed
                  </div>
                )}
              </div>

              <div className={styles.field}>
                <label>Bio</label>
                <textarea
                  value={formData.bandBio}
                  onChange={(e) => handleInputChange('bandBio', e.target.value)}
                  maxLength={500}
                  rows={4}
                ></textarea>
                <div className={styles.helperText}>
                  {formData.bandBio.length}/500 characters
                </div>
              </div>

              <div className={styles.field}>
                <label>Genres *</label>
                {errors.bandGenres && (
                  <div className={styles.errorMessage}>{errors.bandGenres}</div>
                )}
                <div className={styles.genreGrid}>
                  {GENRE_OPTIONS.map(genre => (
                    <button
                      key={genre}
                      type="button"
                      className={`${styles.genreButton} ${
                        formData.bandGenres.includes(genre) ? styles.selected : ''
                      }`}
                      onClick={() => handleGenreToggle(genre)}
                    >
                      {genre}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Contact Information */}
          {activeStep === 2 && (
            <div className={styles.formStep}>
              <h3>Contact Information</h3>
              
              <div className={styles.field}>
                <label>Email</label>
                <input
                  type="email"
                  value={formData.bandEmail}
                  onChange={(e) => handleInputChange('bandEmail', e.target.value)}
                  className={errors.bandEmail ? styles.fieldError : ''}
                />
                {errors.bandEmail && (
                  <div className={styles.errorMessage}>{errors.bandEmail}</div>
                )}
                <div className={styles.helperText}>
                  This email will be visible on your band profile
                </div>
              </div>

              <div className={styles.field}>
                <label>Website</label>
                <input
                  type="url"
                  value={formData.bandWebsite}
                  onChange={(e) => handleInputChange('bandWebsite', e.target.value)}
                  className={errors.bandWebsite ? styles.fieldError : ''}
                />
                {errors.bandWebsite && (
                  <div className={styles.errorMessage}>{errors.bandWebsite}</div>
                )}
              </div>

              <div className={styles.locationFields}>
                <div className={styles.field}>
                  <label>City</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                  />
                </div>

                <div className={styles.field}>
                  <label>State</label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                  />
                </div>

                <div className={styles.field}>
                  <label>Country</label>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) => handleInputChange('country', e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Management Details */}
          {activeStep === 3 && (
            <div className={styles.formStep}>
              <h3>Management Contact Details</h3>
              <p className={styles.stepDescription}>
                These details will be used for band-related communications and won't be publicly visible.
              </p>
              
              <div className={styles.field}>
                <label>Manager/Contact Name *</label>
                <input
                  type="text"
                  value={formData.managerName}
                  onChange={(e) => handleInputChange('managerName', e.target.value)}
                  className={errors.managerName ? styles.fieldError : ''}
                />
                {errors.managerName && (
                  <div className={styles.errorMessage}>{errors.managerName}</div>
                )}
              </div>

              <div className={styles.field}>
                <label>Manager/Contact Email *</label>
                <input
                  type="email"
                  value={formData.managerEmail}
                  onChange={(e) => handleInputChange('managerEmail', e.target.value)}
                  className={errors.managerEmail ? styles.fieldError : ''}
                />
                {errors.managerEmail && (
                  <div className={styles.errorMessage}>{errors.managerEmail}</div>
                )}
              </div>

              <div className={styles.field}>
                <label>Manager/Contact Phone *</label>
                <input
                  type="tel"
                  value={formData.managerPhone}
                  onChange={(e) => handleInputChange('managerPhone', e.target.value)}
                  className={errors.managerPhone ? styles.fieldError : ''}
                />
                {errors.managerPhone && (
                  <div className={styles.errorMessage}>{errors.managerPhone}</div>
                )}
              </div>
            </div>
          )}

          {/* Step 4: Social Links */}
          {activeStep === 4 && (
            <div className={styles.formStep}>
              <h3>Social Media Links</h3>
              <p className={styles.stepDescription}>
                Add your band's social media links to connect with fans and other artists.
              </p>
              
              <div className={styles.field}>
                <div className={styles.socialInputGroup}>
                  <FontAwesomeIcon icon={faInstagram} className={styles.socialIcon} />
                  <input
                    type="url"
                    value={formData.socialLinks.instagramUrl}
                    onChange={(e) => handleSocialLinkChange('instagramUrl', e.target.value)}
                    placeholder="Instagram URL"
                    className={errors['socialLinks.instagramUrl'] ? styles.fieldError : ''}
                  />
                </div>
                {errors['socialLinks.instagramUrl'] && (
                  <div className={styles.errorMessage}>{errors['socialLinks.instagramUrl']}</div>
                )}
              </div>

              <div className={styles.field}>
                <div className={styles.socialInputGroup}>
                  <FontAwesomeIcon icon={faTwitter} className={styles.socialIcon} />
                  <input
                    type="url"
                    value={formData.socialLinks.twitterUrl}
                    onChange={(e) => handleSocialLinkChange('twitterUrl', e.target.value)}
                    placeholder="Twitter URL"
                    className={errors['socialLinks.twitterUrl'] ? styles.fieldError : ''}
                  />
                </div>
                {errors['socialLinks.twitterUrl'] && (
                  <div className={styles.errorMessage}>{errors['socialLinks.twitterUrl']}</div>
                )}
              </div>

              <div className={styles.field}>
                <div className={styles.socialInputGroup}>
                  <FontAwesomeIcon icon={faFacebook} className={styles.socialIcon} />
                  <input
                    type="url"
                    value={formData.socialLinks.facebookUrl}
                    onChange={(e) => handleSocialLinkChange('facebookUrl', e.target.value)}
                    placeholder="Facebook URL"
                    className={errors['socialLinks.facebookUrl'] ? styles.fieldError : ''}
                  />
                </div>
                {errors['socialLinks.facebookUrl'] && (
                  <div className={styles.errorMessage}>{errors['socialLinks.facebookUrl']}</div>
                )}
              </div>

              <div className={styles.field}>
                <div className={styles.socialInputGroup}>
                  <FontAwesomeIcon icon={faYoutube} className={styles.socialIcon} />
                  <input
                    type="url"
                    value={formData.socialLinks.youtubeUrl}
                    onChange={(e) => handleSocialLinkChange('youtubeUrl', e.target.value)}
                    placeholder="YouTube URL"
                    className={errors['socialLinks.youtubeUrl'] ? styles.fieldError : ''}
                  />
                </div>
                {errors['socialLinks.youtubeUrl'] && (
                  <div className={styles.errorMessage}>{errors['socialLinks.youtubeUrl']}</div>
                )}
              </div>

              <div className={styles.field}>
                <div className={styles.socialInputGroup}>
                  <div className={styles.socialIconContainer}>
                    <img src={spotifyLogo} alt="Spotify" className={styles.brandSocialIcon} />
                  </div>
                  <input
                    type="url"
                    value={formData.socialLinks.spotifyUrl}
                    onChange={(e) => handleSocialLinkChange('spotifyUrl', e.target.value)}
                    placeholder="Spotify URL"
                    className={errors['socialLinks.spotifyUrl'] ? styles.fieldError : ''}
                  />
                </div>
                {errors['socialLinks.spotifyUrl'] && (
                  <div className={styles.errorMessage}>{errors['socialLinks.spotifyUrl']}</div>
                )}
              </div>

              <div className={styles.field}>
                <div className={styles.socialInputGroup}>
                  <div className={styles.socialIconContainer}>
                    <img src={appleMusicLogo} alt="Apple Music" className={styles.brandSocialIcon} />
                  </div>
                  <input
                    type="url"
                    value={formData.socialLinks.appleMusicUrl}
                    onChange={(e) => handleSocialLinkChange('appleMusicUrl', e.target.value)}
                    placeholder="Apple Music URL"
                    className={errors['socialLinks.appleMusicUrl'] ? styles.fieldError : ''}
                  />
                </div>
                {errors['socialLinks.appleMusicUrl'] && (
                  <div className={styles.errorMessage}>{errors['socialLinks.appleMusicUrl']}</div>
                )}
              </div>
            </div>
          )}

          {/* Step 5: Upload Photos */}
          {activeStep === 5 && (
            <div className={styles.formStep}>
              <h3>Upload Band Photos</h3>
              <p className={styles.stepDescription}>
                Add profile and cover photos for your band page. You can change these later.
              </p>
              
              <div className={styles.imageSection}>
                <div className={styles.imageUploadContainer}>
                  <h4>Profile Photo</h4>
                  <div className={styles.profilePhotoPreview}>
                    <img 
                      src={profilePicture.preview || defaultBandPic} 
                      alt="Profile Preview"
                      className={styles.profilePreview}
                    />
                    <button 
                      type="button"
                      className={styles.uploadPhotoButton}
                      onClick={handleProfilePicClick}
                    >
                      <FontAwesomeIcon icon={faCamera} />
                      Choose Photo
                    </button>
                    <input
                      type="file"
                      accept="image/*"
                      ref={profilePicRef}
                      onChange={handleProfilePicChange}
                      className={styles.hiddenInput}
                    />
                  </div>
                  <p className={styles.photoTip}>
                    Recommended: Square image, at least 500x500 pixels
                  </p>
                </div>
                
                <div className={styles.imageUploadContainer}>
                  <h4>Cover Photo</h4>
                  <div className={styles.coverPhotoPreview}>
                    <img 
                      src={coverPhoto.preview || defaultCoverPhoto} 
                      alt="Cover Preview"
                      className={styles.coverPreview}
                    />
                    <button 
                      type="button"
                      className={styles.uploadPhotoButton}
                      onClick={handleCoverPhotoClick}
                    >
                      <FontAwesomeIcon icon={faCamera} />
                      Choose Photo
                    </button>
                    <input
                      type="file"
                      accept="image/*"
                      ref={coverPhotoRef}
                      onChange={handleCoverPhotoChange}
                      className={styles.hiddenInput}
                    />
                  </div>
                  <p className={styles.photoTip}>
                    Recommended: 16:9 ratio, at least 1200x675 pixels
                  </p>
                </div>
              </div>
              
              {errors.image && (
                <div className={styles.errorMessage}>{errors.image}</div>
              )}
            </div>
          )}

          {/* Navigation Buttons */}
          <div className={styles.formNavigation}>
            {activeStep > 1 && (
              <button 
                type="button" 
                className={styles.prevButton}
                onClick={handlePrevStep}
                disabled={isLoading}
              >
                Previous
              </button>
            )}
            
            <button 
              type="button" 
              className={styles.nextButton}
              onClick={handleNextStep}
              disabled={isLoading || (activeStep === 1 && usernameChecking)}
            >
              {isLoading ? (
                <span className={styles.buttonSpinner}></span>
              ) : activeStep === 5 ? ( // Changed from 6 to 5
                'Create Band Page'
              ) : (
                'Next'
              )}
            </button>
          </div>
          
          {errors.submit && (
            <div className={styles.submitError}>
              <FontAwesomeIcon icon={faExclamationTriangle} />
              {errors.submit}
            </div>
          )}
        </div>
      </div>

      {/* Image Croppers */}
      {showProfileCropper && selectedImage && (
        <ImageCropper
          image={selectedImage.preview}
          onCropComplete={handleCropComplete}
          onSave={handleSaveCrop}
          onCancel={() => {
            setShowProfileCropper(false);
            if (selectedImage.preview) {
              URL.revokeObjectURL(selectedImage.preview);
            }
            setSelectedImage(null);
            setCropData(null);
          }}
          aspect={1}
          cropShape="round"
          minWidth={500}
        />
      )}

      {showCoverCropper && selectedImage && (
        <ImageCropper
          image={selectedImage.preview}
          onCropComplete={handleCropComplete}
          onSave={handleSaveCrop}
          onCancel={() => {
            setShowCoverCropper(false);
            if (selectedImage.preview) {
              URL.revokeObjectURL(selectedImage.preview);
            }
            setSelectedImage(null);
            setCropData(null);
          }}
          aspect={16/9}
          minWidth={1200}
          minHeight={675}
        />
      )}
    </div>
  );
};

export default BandModal;